/**
 * ProductAuditListContainer
 * --
 */
import { useState, useEffect } from 'react';
import ProductAuditListPresenter from './ProductAuditListPresenter';
import MessageAlert from 'utils';
import { ProductApi, WishlistApi, SendApi } from 'api';
import { useBizSendSMS } from 'hooks/BizHooks';
const DEFAULT_SEARCH_VALUE = {
  product_name: null,
  shop_information_name: null,
};

/**----------------------------------------
 * [Component] 입점업체 상세: 컨테이너
 * ----------------------------------------
 */
const ProductAuditListContainer = ({
  defaultShopId,
  onChangeShopInformation,
  permission,
  productAuditMode,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [shopId, setShopId] = useState(defaultShopId);
  const [productList, setProductList] = useState([]);
  const [searchValue, setSearchValue] = useState(DEFAULT_SEARCH_VALUE);
  /* ===== VARIABLE ===== */
  const { sendSMS } = useBizSendSMS();
  const mutate = {
    SMS: sendSMS,
    PUSH: SendApi.sendPushMessage,
  };
  /* ===== FUNCTION ===== */

  /**--------------------
   * 상품(product) 데이터 변경
   * --------------------
   */
  const handleChangeProduct = async (product_id, key, newStatus) => {
    if (permission !== 'ADMIN') {
      return;
    }
    setLoading(true);
    try {
      if (!newStatus) {
        MessageAlert.warning('값을 입력해주세요');
        setLoading(false);
        return;
      }
      const fid = productList.findIndex((p) => p.product_id === product_id);
      const newData = {
        product_id,
        [key]: newStatus,
      };

      // API Call
      const { status, message } = await ProductApi.updateProduct(newData);
      // 예외처리
      if (status !== 200) throw { message };

      setTimeout(() => {
        MessageAlert.info('변경되었습니다.');
        setLoading(false);
        setProductList((prev) => [
          ...prev.slice(0, fid),
          {
            ...prev[fid],
            ...newData,
          },
          ...prev.slice(fid + 1),
        ]);
      }, 1000);
    } catch (e) {
      setLoading(false);
      console.log('[BrandDetailContainer][handleChangeProduct] Error: ', e);
      MessageAlert.error(e.message);
    }
  };

  /**
   *
   * @param {string} t
   * @param {string | number} v
   */
  const handleChange = (t, v) => {
    setSearchValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };
  const handleSendNotification = async (t, v) => {
    try {
      const type = t.toLocaleUpperCase();
      const msg = `${
        type === 'PUSH' ? '푸쉬알림으' : '문자'
      }로 재입고 알림을 전송 하시겠습니까?`;
      const confirm = (msg) => window.confirm(msg);
      if (!confirm(msg)) return;
      //NOTE: 재입고 알림 목록 조회
      const { status, data } = await WishlistApi.getWishlists({
        product_id: v.product_id,
        type: 'RESTOCK',
        join_tables: '["user", "user.device"]',
      });

      if (status !== 200)
        return MessageAlert.info('회원 정보를 조회하는데 실패했습니다.');

      //NOTE: 데이터 정제
      const userSendData = [];
      for (const wish of data) {
        if (type === 'PUSH') {
          const token = wish?.user?.device?.at(-1)?.push_token;
          if (token) userSendData.push(token);
        }
        if (type === 'SMS') {
          const phone = wish?.user?.user_phone;
          if (phone) userSendData.push(phone);
        }
      }
      //NOTE: 푸쉬 알림 데이터
      const productName = v.product_name?.slice(0, 31) || '상품';
      const message = `${productName} 상품이 재입고 되었습니다.`;
      const notificationValue = {
        SMS: {
          to_phones: userSendData,
          type: 'sms',
          content: {
            sms: {
              message: message,
            },
          },
          is_template: 'N',
        },
        PUSH: {
          device_tokens: userSendData,
          title: message,
          body: message,
        },
      };
      if (!userSendData?.length)
        return MessageAlert.info('재입고 알림을 신청한 회원이 없습니다.');
      //NOTE: API
      const result = await mutate[type](notificationValue[type]);
      if (result.status === 200) {
        MessageAlert.info('재입고 알림을 전송하었습니다.');
      } else {
        MessageAlert.error('재입고 알림 전송 실패했습니다.');
      }
    } catch (e) {
      MessageAlert.error('재입고 알림 전송 실패했습니다.');
    }
  };
  /* ===== HOOKS ===== */

  /**--------------------
   * 입점업체 상품 조회
   * --------------------
   */
  useEffect(() => {
    const call = async () => {
      try {
        const { status, message, data } = await ProductApi.getProducts({
          join_tables: `["shop_information"]`,
          ...(defaultShopId && { shop_information_id: defaultShopId }),
        });
        if (status !== 200) throw { message };
        setProductList(data);
      } catch (e) {
        MessageAlert.error(e.message);
      }
    };

    call();
  }, []);

  /* ===== RENDER ===== */
  return (
    <>
      <ProductAuditListPresenter
        {...{
          loading,
          shopId,
          permission,
          productList,
          onChangeProduct: handleChangeProduct,
          searchValue,
          onChange: handleChange,
          onSendNotification: handleSendNotification,
        }}
      />
    </>
  );
};

/* Default props */
ProductAuditListContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
  productAuditMode: false,
  onChangeShopInformation: () => {},
};
export default ProductAuditListContainer;
